import clsx from "clsx";
import React from "react";

import type { UseForm } from "../../../hooks/use-form";
import { InputBankAccountNumber } from "../../Inputs/InputBankAccountNumber";
import { InputCheckbox } from "../../Inputs/InputCheckbox";
import { InputCpfOrCnpj } from "../../Inputs/InputCpfOrCnpj";
import { InputMask } from "../../Inputs/InputMask";
import { InputPercentage } from "../../Inputs/InputPercentage";
import { InputPhone } from "../../Inputs/InputPhone";
import { InputRadio } from "../../Inputs/InputRadio";
import { InputSelect } from "../../Inputs/InputSelect";
import { InputText } from "../../Inputs/InputText";

const inputComponent = {
  text: InputText,
  password: InputText,
  number: InputText,
  textarea: InputText,
  checkbox: InputCheckbox,
  radio: InputRadio,
  cardNumber: InputMask,
  cardSecurityCode: InputMask,
  cpf: InputMask,
  cep: InputMask,
  cnpj: InputMask,
  date: InputMask,
  hour: InputMask,
  monthYearDate: InputMask,
  cpfOrCnpj: InputCpfOrCnpj,
  percentage: InputPercentage,
  phoneNumber: InputPhone,
  select: InputSelect,
  bankAccount: InputBankAccountNumber,
};

const FormField = ({
  fieldSetting,
  loading,
  loadingValues,
  className,
}: {
  fieldSetting: UseForm["fields"][number];
  className?: string;
  loading?: boolean;
  loadingValues?: boolean;
}) => {
  const { className: customClassName, initialValue, onInput, ...field } = fieldSetting;

  const InputComponent = inputComponent[
    (field.type || "text") as keyof typeof inputComponent
  ] as unknown as typeof React.Component;

  return (
    <InputComponent
      {...field}
      field={field.id}
      id={field.id}
      name={field.id}
      value={field.value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInput(e.target.value, field.id, field.type)}
      className={clsx("mb-2 grow lg:mb-5", className, customClassName)}
      disabled={loading || field.disabled}
      initialvalue={field.type === "select" ? initialValue : undefined}
      loadingValue={loadingValues}
    />
  );
};

interface Props {
  fields: UseForm["fields"];
  loading?: boolean;
  loadingValues?: boolean;
  rowLayoutSettings?: number[];
  baseClassName?: string;
}

export const FormFieldsBase = ({ fields, loading, rowLayoutSettings, loadingValues, baseClassName }: Props) => {
  if (rowLayoutSettings) {
    let index = 0;

    return rowLayoutSettings.map((fieldsPerRow, rowIndex) => {
      const rowFields = fields.slice(index, index + fieldsPerRow);
      index += fieldsPerRow;

      return (
        <div
          key={rowIndex}
          className={clsx("w-full lg:flex lg:gap-3", {
            "lg:mb-2": rowFields.some(({ errorMessage }) => errorMessage),
          })}
        >
          {rowFields.map((field) => (
            <FormField
              key={field.id}
              fieldSetting={field}
              className={clsx(
                {
                  "lg:max-w-[calc(50%_-_.75rem)]": fieldsPerRow === 2,
                },
                field.className,
              )}
              loading={loading}
              loadingValues={loadingValues}
            />
          ))}
        </div>
      );
    });
  }

  return fields.map((field) => (
    <FormField
      key={field.id}
      fieldSetting={field}
      loading={loading}
      loadingValues={loadingValues}
      className={clsx({ "lg:pb-2": field.errorMessage }, baseClassName)}
    />
  ));
};
